import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Building2, Trash2, Users } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { TenantUsersDialog } from './TenantUsersDialog';
import {
  useTenantsList,
  useUpdateTenantStatus,
  useDeleteTenant,
  useTenantModules,
  useSetTenantModule,
} from '@/hooks/useAdmin';
import type { AdminTenant } from '@/api/admin.api';

const STATUS_LABEL: Record<AdminTenant['status'], string> = {
  active: 'Ativo',
  suspended: 'Suspenso',
  trial: 'Trial',
};

const STATUS_VARIANT: Record<
  AdminTenant['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  active: 'default',
  trial: 'outline',
  suspended: 'destructive',
};

const MODULES = [
  { key: 'campanhas', label: 'Campanhas' },
  { key: 'financeiro', label: 'UpLog' },
  { key: 'rcs', label: 'RCS' },
] as const;

function ModuleToggles({ tenantId }: { tenantId: string }) {
  const { data: enabled = [] } = useTenantModules(tenantId);
  const { mutate: setModule, isPending } = useSetTenantModule();

  return (
    <div className="flex flex-col gap-1.5">
      {MODULES.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-2">
          <Switch
            checked={enabled.includes(key)}
            disabled={isPending}
            onCheckedChange={(checked) =>
              setModule({ id: tenantId, module: key, enabled: checked })
            }
          />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-10" />
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-8" />
        </div>
      ))}
    </div>
  );
}

export function TenantList() {
  const { data: tenants = [], isLoading } = useTenantsList();
  const { mutate: updateStatus, isPending: updatingStatus } = useUpdateTenantStatus();
  const { mutate: deleteTenant, isPending: deleting } = useDeleteTenant();
  const [deleteTarget, setDeleteTarget] = useState<AdminTenant | null>(null);
  const [usersTarget, setUsersTarget] = useState<AdminTenant | null>(null);

  if (isLoading) return <ListSkeleton />;

  if (tenants.length === 0) {
    return <EmptyState icon={Building2} title="Nenhum cliente cadastrado" />;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Empresa</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead className="text-right">Usuários</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Módulos</TableHead>
            <TableHead>Criado</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenants.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="font-medium">{t.name}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">{t.slug}</TableCell>
              <TableCell className="text-right">{t.userCount}</TableCell>
              <TableCell>
                <Select
                  value={t.status}
                  onValueChange={(v) =>
                    updateStatus({ id: t.id, status: v as AdminTenant['status'] })
                  }
                  disabled={updatingStatus}
                >
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue>
                      <Badge variant={STATUS_VARIANT[t.status]} className="text-xs">
                        {STATUS_LABEL[t.status]}
                      </Badge>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="suspended">Suspenso</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <ModuleToggles tenantId={t.id} />
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {format(new Date(t.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() => setUsersTarget(t)}
                  >
                    <Users className="h-3.5 w-3.5" />
                    Usuários
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget(t)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title="Remover cliente"
        description={`Remover "${deleteTarget?.name}"? Todos os dados do tenant serão apagados permanentemente.`}
        confirmLabel="Remover"
        variant="destructive"
        loading={deleting}
        onConfirm={() => {
          if (deleteTarget) {
            deleteTenant(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }
        }}
      />

      <TenantUsersDialog
        tenant={usersTarget}
        onOpenChange={(open) => {
          if (!open) setUsersTarget(null);
        }}
      />
    </>
  );
}
